import { useCallback, useEffect, useState } from 'react';
import { useMe } from '../../auth/MeContext';
import {
  operatorConsole,
  type ConsoleSection,
  type TeamMember,
  type TeamResponse,
} from '../../lib/api/operatorConsole';
import { backendMessage } from '../TestPlayers/backendMessage';
import { AddMemberForm } from './AddMemberForm';
import { OneTimePasswordPanel } from './OneTimePasswordPanel';
import { TeamMemberRow } from './TeamMemberRow';

interface Issued {
  email: string;
  password: string | null;
}

export function TeamPage() {
  const { me } = useMe();
  const [data, setData] = useState<TeamResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [issued, setIssued] = useState<Issued | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setData(await operatorConsole.listTeam());
    } catch (err) {
      setProblem(`Failed to load the team: ${backendMessage(err) ?? 'unknown error'}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  function flashSuccess(message: string) {
    setSuccess(message);
    setTimeout(() => setSuccess(null), 2500);
  }

  async function handleAdd(email: string, permissions: ConsoleSection[]): Promise<boolean> {
    setProblem(null);
    setIssued(null);
    try {
      const res = await operatorConsole.addTeamMember(email, permissions);
      setIssued({ email: res.member.email, password: res.temporary_password ?? null });
      await load();
      return true;
    } catch (err) {
      setProblem(backendMessage(err) ?? 'Could not add this person.');
      // The account may still have been created if the answer was lost on the
      // way back, so the list is refreshed either way — an owner who sees an
      // error and a new row knows to reset that person's password rather than
      // adding them twice.
      await load();
      return false;
    }
  }

  async function handleSave(
    member: TeamMember,
    permissions: ConsoleSection[],
  ): Promise<boolean> {
    setProblem(null);
    setBusy(true);
    try {
      await operatorConsole.updateTeamMember(member.id, { permissions });
      flashSuccess(`${member.email} updated`);
      await load();
      return true;
    } catch (err) {
      setProblem(backendMessage(err) ?? 'Could not change this access.');
      return false;
    } finally {
      setBusy(false);
    }
  }

  function handleResetPassword(member: TeamMember) {
    const confirmed = window.confirm(
      `Issue a new password for ${member.email}? It is shown once, and they are signed out of the console everywhere.`,
    );
    if (!confirmed) return;

    setProblem(null);
    setIssued(null);
    setBusy(true);
    operatorConsole
      .resetTeamMemberPassword(member.id)
      .then((res) => {
        setIssued({ email: member.email, password: res.temporary_password ?? null });
        return load();
      })
      .catch((err: unknown) => {
        setProblem(backendMessage(err) ?? 'Could not reset the password.');
      })
      .finally(() => setBusy(false));
  }

  function handleRevoke(member: TeamMember) {
    const confirmed = window.confirm(
      `Remove ${member.email} from the console? They are signed out immediately and lose access to every section.`,
    );
    if (!confirmed) return;

    setProblem(null);
    setBusy(true);
    operatorConsole
      .removeTeamMember(member.id)
      .then(() => {
        flashSuccess(`${member.email} removed`);
        return load();
      })
      .catch((err: unknown) => {
        setProblem(backendMessage(err) ?? 'Could not remove this person.');
      })
      .finally(() => setBusy(false));
  }

  const sections = data?.permissions.sections ?? [];
  const presets = data?.permissions.presets ?? {};

  return (
    <div className="p-6">
      <div className="mb-5 flex items-baseline justify-between gap-4">
        <div>
          <h1 className="text-[20px] font-semibold text-[var(--co-text)]">Team</h1>
          <p className="text-[12px] text-[var(--co-muted)]">
            Who from your side can sign into this console, and what they can reach. A change takes
            effect the moment they reload the console — they do not need to sign in again.
          </p>
        </div>
        {success && (
          <span className="rounded bg-[var(--co-success)] px-2 py-1 text-[11px] font-medium text-white">
            {success}
          </span>
        )}
      </div>

      {problem && (
        <div
          role="alert"
          className="mb-5 max-w-3xl rounded-xl px-4 py-3 text-[12px]"
          style={{
            color: 'var(--co-error)',
            backgroundColor: 'color-mix(in srgb, var(--co-error) 10%, transparent)',
          }}
        >
          {problem}
        </div>
      )}

      {issued && (
        <OneTimePasswordPanel
          email={issued.email}
          password={issued.password}
          onDismiss={() => setIssued(null)}
        />
      )}

      <AddMemberForm sections={sections} presets={presets} onSubmit={handleAdd} />

      <div className="max-w-3xl overflow-hidden rounded-xl border border-[var(--co-border)] bg-[var(--co-surface)]">
        {loading ? (
          <p className="p-4 text-[12px] text-[var(--co-muted)]">Loading…</p>
        ) : (
          <ul>
            {(data?.members ?? []).map((member) => (
              <TeamMemberRow
                key={member.id}
                member={member}
                isSelf={member.user_id === me?.owner.id}
                sections={sections}
                presets={presets}
                busy={busy}
                onSave={handleSave}
                onResetPassword={handleResetPassword}
                onRevoke={handleRevoke}
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
